import { useCallback, useEffect, useState } from "react";
import type { ChangeSetInfo } from "@racler/protocol";
import type { ReviewController, Decide } from "../lib/useReview.ts";
import { History, Plus } from "lucide-react";
import type { ChatController } from "../lib/useChat.ts";
import type { OpenFiles } from "../lib/useOpenFiles.ts";
import type { Selection } from "../lib/composer.ts";
import { MessageList } from "./ChatMessages.tsx";
import { ChatHistory } from "./ChatHistory.tsx";
import { Composer } from "./Composer.tsx";
import { IconButton } from "./IconButton.tsx";
import { RunStatusBar } from "./RunStatusBar.tsx";
import { api } from "../lib/api.ts";

interface Props {
  machineId: string;
  projectId?: string | undefined;
  files?: OpenFiles | undefined;
  /** The single chat/session state of the Project (shared with the optional History panel). */
  controller: ChatController;
  /** History is embedded in this pane (the default) unless it is shown as its own, standalone panel. */
  historyStandalone?: boolean;
  /** Review state shared with the editor area (diff review opens there, inside the IDE). */
  review?: ReviewController | undefined;
}

/**
 * Agent pane (Ask / Plan / Edit): Ask and Plan can only read; Edit can only PROPOSE changes, which are reviewed in
 * the editor area and applied only after explicit approval. Model credentials live on
 * the user's machine; this pane only ever sees model names and streamed text.
 *
 *   header (title, new chat, history) → conversation (flexible) → composer pinned to the bottom
 */
export function AgentPane({
  machineId,
  projectId,
  files,
  controller,
  historyStandalone = false,
  review,
}: Props) {
  const {
    chat,
    sessions,
    providers,
    lastModel,
    error,
    lastPrompt,
    send,
    stop,
    open,
    newChat,
    loadModels,
    remove,
    decide,
    decideCommand,
    cancelCommand,
    activeEnv,
    refreshEnv,
  } = controller;
  const [selection, setSelection] = useState<Selection | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [finalError, setFinalError] = useState<string | null>(null);
  const [finalCs, setFinalCs] = useState<ChangeSetInfo | null>(null);
  const running = chat.activeRunId !== null || chat.activeRun?.status === "running";
  const noProviders = providers !== null && !providers.some((p) => p.configured);
  const title = sessions.find((s) => s.id === chat.sessionId)?.title;

  // Auto-finalize the autonomous environment as soon as it completes
  useEffect(() => {
    let cancelled = false;
    if (activeEnv && activeEnv.state === "completed" && !activeEnv.changeSetId && projectId) {
      void (async () => {
        try {
          const res = await api.finalizeEnvironment(machineId, projectId, activeEnv.id);
          if (!cancelled) {
            setFinalCs(res.changeSet);
            await refreshEnv();
          }
        } catch (e) {
          if (!cancelled) {
            const message = e instanceof Error ? e.message : String(e);
            if (!message.includes("the isolated run made no applicable changes")) {
              setFinalError(message);
            }
          }
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [activeEnv, machineId, projectId, refreshEnv]);

  useEffect(() => {
    if (!activeEnv || activeEnv.state !== "completed") {
      setFinalCs(null);
      setFinalError(null);
    }
  }, [activeEnv]);

  const autonomousCs =
    (activeEnv?.changeSetId ? chat.changeSets.find((c) => c.id === activeEnv.changeSetId) : null) ?? finalCs;
  const count = autonomousCs?.files.length;
  const hasReviewableChangeSet = Boolean(autonomousCs && count !== undefined && count > 0);
  const autonomousSummary = hasReviewableChangeSet
    ? `Agent finished · ${count} ${count === 1 ? "file" : "files"} changed`
    : "Agent finished · No changes made";

  const handleReview = async () => {
    if (!projectId || !activeEnv) return;
    try {
      setFinalError(null);
      let targetCs = autonomousCs;
      if (!targetCs || targetCs.status !== "pending_approval") {
        const res = await api.finalizeEnvironment(machineId, projectId, activeEnv.id);
        targetCs = res.changeSet;
        setFinalCs(res.changeSet);
        await refreshEnv();
      }
      review?.open(targetCs.id);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      if (!message.includes("the isolated run made no applicable changes")) {
        setFinalError(message);
      }
    }
  };

  const handleDecide: Decide = useCallback(
    async (kind: "approve" | "reject", targetCs: ChangeSetInfo): Promise<ChangeSetInfo> => {
      const isAutonomousFinal = Boolean(
        activeEnv &&
          activeEnv.state === "completed" &&
          (activeEnv.changeSetId === targetCs.id || targetCs.id === finalCs?.id) &&
          projectId,
      );

      if (isAutonomousFinal && projectId && activeEnv) {
        if (kind === "approve") {
          const res = await api.applyEnvironment(
            machineId,
            projectId,
            activeEnv.id,
            targetCs.contentHash ?? undefined,
          );
          await refreshEnv();
          if (!res.ok || res.changeSet.status !== "applied") {
            throw new Error(
              `Apply ${res.changeSet.status}: ${res.changeSet.message ?? "the project changed since the base commit"}. The isolated worktree is kept.`,
            );
          }
          setFinalCs(null);
          return res.changeSet;
        } else {
          await api.discardEnvironment(machineId, projectId, activeEnv.id);
          await refreshEnv();
          setFinalCs(null);
          return { ...targetCs, status: "rejected" };
        }
      }
      return decide(kind, targetCs);
    },
    [activeEnv, projectId, machineId, refreshEnv, decide, finalCs],
  );

  const provide = review?.provide;
  useEffect(() => {
    const list =
      finalCs && !chat.changeSets.some((c) => c.id === finalCs.id)
        ? [...chat.changeSets, finalCs]
        : chat.changeSets;
    provide?.(list, handleDecide);
  }, [provide, chat.changeSets, handleDecide, finalCs]);

  const onSelection = useCallback((s: Selection | null) => setSelection(s), []);
  const retry = () => {
    if (running) return;
    const promptToRetry =
      lastPrompt ?? [...chat.messages].reverse().find((m) => m.role === "user")?.content;
    if (promptToRetry && selection) void send(promptToRetry, selection);
  };

  return (
    <div className="agent-pane" data-testid="agent-pane">
      <div className="pane-head">
        <span className="pane-title agent-title" title={title}>
          Agent{title ? <span className="muted"> · {title}</span> : null}
        </span>
        <span className="pane-tools">
          <IconButton
            icon={Plus}
            label="New chat"
            onClick={() => {
              newChat();
              setShowHistory(false);
            }}
            disabled={running}
            testId="new-chat"
          />
          {!historyStandalone && (
            <IconButton
              icon={History}
              label="Chat history"
              active={showHistory}
              onClick={() => setShowHistory((v) => !v)}
              testId="history-toggle"
            />
          )}
        </span>
      </div>
      {showHistory && !historyStandalone ? (
        <ChatHistory
          sessions={sessions}
          activeId={chat.sessionId}
          runningId={chat.activeRunId ? chat.sessionId : null}
          onSelect={(id) => {
            void open(id);
            setShowHistory(false);
          }}
          onDelete={async (id) => void (await remove(id))}
          onNew={() => {
            newChat();
            setShowHistory(false);
          }}
          onClose={() => setShowHistory(false)}
        />
      ) : (
        <>
          {noProviders && (
            <div className="agent-notice small" data-testid="no-provider">
              No model provider is configured on this machine. Run <code>racler provider add openrouter</code>{" "}
              there, then{" "}
              <button className="link-btn" onClick={() => void loadModels()}>
                refresh
              </button>
              .
            </div>
          )}
          {error && (
            <div className="msg-error" role="alert" data-testid="chat-error">
              {error}
            </div>
          )}
          {activeEnv && activeEnv.state === "active" && (
            <div className="dry-run-badge" data-testid="dry-run-badge" style={{ margin: "4px 8px" }}>
              <span>Dry Run · Worktree</span>
              <span className="muted small">Base {activeEnv.baseCommit.slice(0, 7)}</span>
            </div>
          )}
          <RunStatusBar
            activeRun={chat.activeRun}
            running={running}
            onStop={() => void stop()}
            onRetry={retry}
          />
          <MessageList
            messages={chat.messages}
            onRetry={retry}
            changeSets={chat.changeSets}
            onReview={review?.open}
            commands={chat.commands}
            commandActions={{
              onApprove: (c) => decideCommand("approve", c),
              onDeny: (c) => decideCommand("deny", c),
              onCancel: cancelCommand,
            }}
          />
        </>
      )}
      {!showHistory && activeEnv && activeEnv.state === "completed" && (
        <div className="autonomous-result-bar dry-run-banner" data-testid="autonomous-result-bar">
          <span className="result-text" data-testid="autonomous-result-summary">
            {autonomousSummary}
          </span>
          {finalError && !finalError.includes("the isolated run made no applicable changes") && (
            <span className="msg-error small" role="alert" data-testid="dry-run-error">
              {finalError}
            </span>
          )}
          {hasReviewableChangeSet && (
            <button
              type="button"
              className="action-btn"
              data-testid="review-changes"
              onClick={() => void handleReview()}
            >
              Review changes
            </button>
          )}
        </div>
      )}
      <Composer
        providers={providers}
        scope={machineId}
        machineId={machineId}
        projectId={projectId}
        files={files}
        lastModel={chat.sessionId ? null : lastModel}
        running={running}
        onSend={(text, sel, contextItems, envType, envId) => void send(text, sel, contextItems, envType, envId)}
        onStop={() => void stop()}
        onSelection={onSelection}
      />
    </div>
  );
}
